import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { features } from '../constants-types/constants';
import { PI_VERSION } from '../constants-types/constants';
import { RoleType } from '../constants-types/Enums';
import { IconType } from '../core-data-model/IconType';
import { AuthenticationService } from '../core-services/Authentication.service';
import {
    SelfIdentityDataService,
    SelfIdentityMatchState
} from '../core-services/SelfIdentity.dataservice';
import { ConfigurationService } from '../system-configuration/Configuration.service';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations/Translation.service';

enum CardMenuOption {
    Cognitive,
    Profile,
    Administration,
    FBC
}

type CardItem = {
    onClick: (event?: any) => void;
    text: string;
    icon: IconType;
};

type CardOptionsMap = Map<CardMenuOption, CardItem>;
@Component({
    selector: 'user-dropdown',
    template: `
        <div class="pi-dropdown" (clickOutside)="focusLost()">
            <div
                #clickArea
                (click)="showCard = !showCard"
                class="pi-dropdown_clickPrompt"
                tooltip-on-click
                [tooltipContent]="userCard"
                [tooltipPlacement]="'bottom'"
                [tooltipShow]="showCard"
            >
                {{ initData.user.fullName() }}
                <abstract-icon
                    class="pi-dropdown_caretIcon"
                    [iconType]="IconType.ChevronDown"
                ></abstract-icon>
                <tooltip-content #userCard
                    ><div class="userCard">
                        <div
                            class="title-section layout--center"
                            style="padding-bottom:10px;"
                        >
                            @if (showMe) {
                                <abstract-icon
                                    [iconType]="getRefProfileIcon()"
                                    [iconSize]="'35px'"
                                    style="padding-right: 10px;"
                                ></abstract-icon>
                            }
                            {{ initData.user.fullName() }}
                        </div>
                        <!-- Cognitive is a special snowflake via styling hence it having its own element plus being hidden below-->
                        @if (cardOptionsMap.has(CardMenuOption.Cognitive)) {
                            <div
                                class="copy-md"
                                style="color:#33A8D3;"
                                (click)="
                                    cardOptionsMap
                                        .get(CardMenuOption.Cognitive)
                                        .onClick($event)
                                "
                            >
                                <abstract-icon
                                    [iconType]="
                                        cardOptionsMap.get(
                                            CardMenuOption.Cognitive
                                        ).icon
                                    "
                                    [iconSize]="'13px'"
                                    class="eyeIconBlue"
                                ></abstract-icon>
                                {{
                                    cardOptionsMap.get(CardMenuOption.Cognitive)
                                        .text
                                }}
                            </div>
                        }
                        <ul class="copy-md" style="padding-top: 15px;">
                            @for (
                                option of cardOptionsMap | keyvalue;
                                track option
                            ) {
                                <li
                                    (click)="option.value.onClick($event)"
                                    [ngClass]="{
                                        displayNone:
                                            option.key ===
                                            CardMenuOption.Cognitive
                                    }"
                                >
                                    {{ option.value.text }}
                                    <abstract-icon
                                        [iconType]="option.value.icon"
                                        [iconSize]="'13px'"
                                        style="margin-left: auto; stroke: #91899E"
                                    ></abstract-icon>
                                </li>
                            }
                        </ul>
                        <button
                            class="button button-tertiary button-sm"
                            style="margin: 15px auto"
                            (click)="logout()"
                        >
                            {{ TR.key('label.common-terms.logout') }}
                        </button>
                        <span class="layoutCenterAlign color-neutral-700">{{
                            getPiVersion()
                        }}</span>
                    </div></tooltip-content
                >
            </div>
        </div>
    `,
    styles: [
        `
            .displayNone {
                display: none;
            }
        `
    ],
    standalone: false
})
export class UserDropdownComponent implements OnInit {
    cardOptionsMap: CardOptionsMap = new Map();
    MatchState = SelfIdentityMatchState;
    IconType = IconType;
    CardMenuOption = CardMenuOption;
    showMe = false;
    showCard = false;

    constructor(
        private router: Router,
        public TR: TranslationService,
        public initData: InitDataService,
        public userProfileService: UserProfileSettingsService,
        public auth: AuthenticationService,
        public config: ConfigurationService,
        public selfIdentityService: SelfIdentityDataService,
        private moduleAccess: ModuleAccessService
    ) {}

    ngOnInit() {
        this.cardOptionsMap = this.getCardOptionsMap();

        // Need to update the dropdown data when the cognitive flag is flipped
        this.userProfileService.userProfileSetting$.subscribe(() => {
            this.cardOptionsMap = this.getCardOptionsMap();
        });

        this.selfIdentityService.checkIdentity.then(() => {
            if (
                !this.initData.isReadOnly() &&
                this.selfIdentityService.matchState ===
                    this.MatchState.MatchExists &&
                this.selfIdentityService.self
            ) {
                this.showMe = true;
            }
        });
    }

    getCardOptionsMap() {
        const cardMap: CardOptionsMap = new Map();

        if (this.userProfileService.isCognitiveEnabled()) {
            cardMap.set(CardMenuOption.Cognitive, {
                onClick: () => this.toggleShowCognitiveData(),
                text: this.setCognitiveDataLabel(),
                icon: this.setCognitiveIcon()
            });
        }

        if (this.canAccessAdministrationPage()) {
            cardMap.set(CardMenuOption.Administration, {
                onClick: () => this.goToAdministrationPage(),
                text: this.TR.key('administration.linkTo'),
                icon: IconType.ArrowRight
            });
        }

        if (
            this.initData.user.isNetworkUser &&
            this.initData.hasAccess(features.forceUrlPartners)
        ) {
            cardMap.set(CardMenuOption.FBC, {
                onClick: () =>
                    window.open(this.config.getForceBCNetworkUrl(), '_blank'),
                text: this.TR.key('ForceBC'),
                icon: IconType.LearnNew
            });
        }

        if (this.initData.hasAccess(features.forceUrlHq)) {
            cardMap.set(CardMenuOption.FBC, {
                onClick: () =>
                    window.open(this.config.getForceBCHQUrl(), '_blank'),
                text: this.TR.key('ForceBCHQ'),
                icon: IconType.LearnNew
            });
        }

        // Will be brought in once all the required APIs are ready
        cardMap.set(CardMenuOption.Profile, {
            onClick: () => this.goToMyProfilePage(),
            text: this.TR.key('text.common-terms.myprofile'),
            icon: IconType.ArrowRight
        });

        return cardMap;
    }

    focusLost() {
        this.showCard = false;
    }

    logout() {
        // TODO: Eventually to be replaced by Logout Page on the Beta Website
        // Redirecting to the Account Controllers Signout Action

        // flush settings needed to account for auth redirect skipping angular lifecycle hooks
        // TODO: in refactor of auth service flow make sure lifecycle hooks work
        this.auth.logout();
    }

    toggleShowCognitiveData() {
        this.userProfileService.showCognitive =
            !this.userProfileService.showCognitive;

        // Need to update the text for cog toggle
        this.cardOptionsMap = this.getCardOptionsMap();
    }

    setCognitiveDataLabel() {
        return this.userProfileService.showCognitive
            ? `${this.TR.key('label.common-terms.hide.cognitive')}`
            : `${this.TR.key('label.common-terms.show.cognitive')}`;
    }

    setCognitiveIcon(): IconType {
        return this.userProfileService.showCognitive
            ? IconType.HideEye
            : IconType.ShowEye;
    }

    canAccessAdministrationPage() {
        return (
            (this.isSelfServeAccountOwner() ||
                this.isNonSelfServePowerUserOrAbove()) &&
            !this.moduleAccess.hasDesign2 &&
            // Do not allow access if the user has Diagnose standalone and
            // Diagnose 2.0
            // This logic is duplicated in pi-spa in the administration
            // route guard.
            !(
                this.moduleAccess.isDiagnoseStandalone &&
                this.moduleAccess.isDiagnoseSelfServe
            )
        );
    }

    goToAdministrationPage() {
        if (this.initData.hasPermissionLevelOrAbove(RoleType.AccountAdmin)) {
            this.router.navigate(['/administration']);
        } else {
            this.router.navigate(['/administration/folders']);
        }
    }

    goToMyProfilePage() {
        this.router.navigate(['/myprofile']);
    }

    getPiVersion() {
        return `${this.TR.key('footer.version')} ${PI_VERSION}`;
    }

    getRefProfileIcon(): IconType {
        if (
            this.selfIdentityService.matchState !==
            SelfIdentityMatchState.MatchExists
        ) {
            return IconType.Unknown;
        }
        if (this.selfIdentityService.self.referencePattern) {
            const iconType =
                this.selfIdentityService.self.referencePattern?.icon
                    ?.abstractIconSVG;
            if (!iconType) {
                return IconType.Hourglass;
            }
            return iconType;
        }
    }

    private isSelfServeAccountOwner(): boolean {
        return (
            (this.moduleAccess.isTeamDiscoverySelfServe ||
                this.moduleAccess.isHireHeroSelfServe) &&
            this.initData.isAccountOwner()
        );
    }

    private isNonSelfServePowerUserOrAbove(): boolean {
        return (
            this.initData.user.hasPermissionLevelOrGreater(
                RoleType.PowerUser
            ) &&
            !(
                this.moduleAccess.isTeamDiscoverySelfServe ||
                this.moduleAccess.isHireHeroSelfServe
            )
        );
    }
}
